/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { MaterialSpec, RampSpec, TableInfoSpec } from "../../../shared/contracts";
import { RampImageAlignment, RampType } from "../../core/enums";
import { type IPhysicalData, ItemData, toDragPoints } from "../../core/item-data";
import { resolvePhysics } from "../../core/material";
import type { DragPoint } from "../../math/dragpoint";
import { f4 } from "../../math/float";

export class RampData extends ItemData implements IPhysicalData {
  public dragPoints: DragPoint[] = [];
  public elasticity: number = 0.3;
  public elasticityFalloff: number = 0;
  public friction: number = 0.3;
  public hitEvent: boolean = false;
  public heightBottom: number = 0;
  public heightTop: number = f4(50);
  public imageAlignment: number = RampImageAlignment.ImageModeWorld;
  public imageWalls: boolean = true;
  public isCollidable: boolean = true;
  public isVisible: boolean = true;
  public leftWallHeight: number = f4(62);
  public leftWallHeightVisible: number = f4(30);
  /** The spec values are final, so the material does not overwrite them. */
  public overwritePhysics: boolean = true;
  public rampType: number = RampType.RampTypeFlat;
  public rightWallHeight: number = f4(62);
  public rightWallHeightVisible: number = f4(30);
  public scatter: number = 0;
  public szImage?: string;
  public szMaterial?: string;
  public szPhysicsMaterial?: string;
  public threshold: number = 2.0;
  public widthBottom: number = f4(75);
  public widthTop: number = f4(60);
  public wireDiameter: number = f4(8);
  public wireDistanceX: number = f4(38);
  public wireDistanceY: number = f4(88);

  public constructor(itemName: string) {
    super(itemName);
  }

  /**
   * Builds the data from a table spec.
   * Physics values that the spec leaves out come from the material, then from the table.
   */
  public static fromSpec(
    spec: RampSpec,
    info: TableInfoSpec,
    materials?: ReadonlyMap<string, MaterialSpec>,
  ): RampData {
    const data = new RampData(spec.name);
    const phys = resolvePhysics(spec, info, materials);
    data.dragPoints = toDragPoints(spec.dragPoints);
    data.heightBottom = f4(spec.heightBottom);
    data.heightTop = f4(spec.heightTop);
    data.widthBottom = f4(spec.widthBottom);
    data.widthTop = f4(spec.widthTop);
    // upstream: a width of 0 on one end is a width of 0.1
    if (data.widthTop === 0 && data.widthBottom > 0) {
      data.widthTop = 0.1;
    }
    if (data.widthBottom === 0 && data.widthTop > 0) {
      data.widthBottom = 0.1;
    }
    data.rampType = spec.rampType ?? data.rampType;
    data.leftWallHeight = f4(spec.leftWallHeight ?? data.leftWallHeight);
    data.rightWallHeight = f4(spec.rightWallHeight ?? data.rightWallHeight);
    data.leftWallHeightVisible = f4(spec.leftWallHeightVisible ?? data.leftWallHeightVisible);
    data.rightWallHeightVisible = f4(spec.rightWallHeightVisible ?? data.rightWallHeightVisible);
    data.wireDiameter = f4(spec.wireDiameter ?? data.wireDiameter);
    data.wireDistanceX = f4(spec.wireDistanceX ?? data.wireDistanceX);
    data.wireDistanceY = f4(spec.wireDistanceY ?? data.wireDistanceY);
    data.hitEvent = spec.hitEvent ?? data.hitEvent;
    data.isCollidable = spec.isCollidable ?? true;
    data.isVisible = spec.isVisible ?? true;
    data.szMaterial = spec.szMaterial;
    data.szPhysicsMaterial = spec.szMaterial;
    data.elasticity = phys.elasticity;
    data.elasticityFalloff = phys.elasticityFalloff;
    data.friction = phys.friction;
    data.scatter = phys.scatter;
    data.threshold = phys.threshold ?? data.threshold;
    return data;
  }
}
